import { AppointmentStatusCodes } from '@lmt/shared-types';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React from 'react';
import { PatientVisitItem, VisitInfo } from './PatientVisitItem';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { PatientPanelHeader } from './PatientPanelHeader';
import { EmptyBlock } from './EmptyBlock';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';

import styles from './PatientVisits.module.scss';

export interface PatientVisitsProps {
  patientId: number;
  patientVisits: VisitInfo[];
  openCalendarTab: (date: Date) => Promise<void>;
  openMeasurementTab: (
    caseId: number,
    visitId: number,
    measurementId: number,
  ) => void;
}

export const PatientVisitsPanel: React.FC<PatientVisitsProps> = (props) => {
  const { patientId, patientVisits } = props;
  const i18n = useScopedTranslation('patients.overview.visits');
  const forms = useGlobalForms();

  if (patientVisits.length === 0) {
    return (
      <div className={styles.emptyContainer} data-test-id={'patient-visits'}>
        <EmptyBlock
          title={i18n.t('title')}
          text={i18n.t('empty')}
          buttonTitle={i18n.t('add_button')}
          onClick={() =>
            forms.openVisitCreating(undefined, undefined, patientId)
          }
        />
      </div>
    );
  }

  const completedVisits = patientVisits.filter(
    (visit) => visit.statusId !== AppointmentStatusCodes.Planned,
  );

  return (
    <div className={styles.container} data-test-id={'patient-visits'}>
      <PatientPanelHeader title={i18n.t('title')} className={styles.header}>
        <UniLink
          title={i18n.t('add_button')}
          color={ButtonColor.Primary}
          onClick={() =>
            forms.openVisitCreating(undefined, undefined, patientId)
          }
        />
      </PatientPanelHeader>
      <div className={styles.data}>
        <div
          className={styles.itemsContainer}
          data-test-id="planned-visits-container"
        >
          {patientVisits
            .filter(
              (visit) =>
                visit.statusId === AppointmentStatusCodes.Planned ||
                visit.statusId === AppointmentStatusCodes.Confirmed,
            )
            .map((visit) => {
              return (
                <div key={`${visit.id}`}>
                  <PatientVisitItem
                    visit={visit}
                    patientId={patientId}
                    openCalendarTab={props.openCalendarTab}
                    openMeasurementTab={props.openMeasurementTab}
                  />
                </div>
              );
            })}
        </div>
        {completedVisits.length > 0 && (
          <div
            className={styles.completedVisitsHeader}
            data-test-id="completed-visits-header"
          >
            {i18n.t('completed_visits')}
          </div>
        )}
        <div
          className={styles.itemsContainer}
          data-test-id="completed-visits-container"
        >
          {patientVisits
            .filter(
              (visit) =>
                visit.statusId !== AppointmentStatusCodes.Planned &&
                visit.statusId !== AppointmentStatusCodes.Confirmed,
            )
            .map((visit) => {
              return (
                <div key={`${visit.id}`}>
                  <PatientVisitItem
                    visit={visit}
                    patientId={patientId}
                    openCalendarTab={props.openCalendarTab}
                    openMeasurementTab={props.openMeasurementTab}
                  />
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
